import PropTypes from 'prop-types';
// @mui
import { alpha, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import { Grid } from '@mui/material';
import Typography from '@mui/material/Typography';
// auth
import { useAuthContext } from 'src/auth/hooks';
// routes
import { paths } from 'src/routes/paths';
// hooks
import { useResponsive } from 'src/hooks/use-responsive';
// theme
import { bgGradient } from 'src/theme/css';
// components
import Logo from 'src/components/logo';
import HelthImg from '../../assets/illustrations/HMMain.jpg'
import BG from '../../assets/skyline 4.avif'
import defaultImageHomepage from '../../assets/defaultImageHomepage.jpg'
// ----------------------------------------------------------------------

const METHODS = [
  {
    id: 'jwt',
    label: 'Jwt',
    path: paths.auth.jwt.login,
    icon: '/assets/icons/auth/ic_jwt.svg',
  },
  {
    id: 'firebase',
    label: 'Firebase',
    path: paths.auth.firebase.login,
    icon: '/assets/icons/auth/ic_firebase.svg',
  },
  {
    id: 'amplify',
    label: 'Amplify',
    path: paths.auth.amplify.login,
    icon: '/assets/icons/auth/ic_amplify.svg',
  },
  {
    id: 'auth0',
    label: 'Auth0',
    path: paths.auth.auth0.login,
    icon: '/assets/icons/auth/ic_auth0.svg',
  },
];

export default function AuthClassicLayout({ children, image, title }) {
  const { method } = useAuthContext();
  const theme = useTheme();
  const isMobile = useResponsive('down', 'md');

  const renderLogo = (
    <Logo
      sx={{
        zIndex: 9,
        position: 'absolute',
        m: { xs: 2, md: 5 },
      }}
    />
  );

  // Mobile layout (like KPJ Healthcare)
  if (isMobile) {
    return (
      <Box
        sx={{
          position: 'relative',
          minHeight: '100vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Background image - 40% height */}
        <Box
          component="img"
          alt="Background"
          src={defaultImageHomepage}
          sx={{
            width: '100%',
            height: '40vh',
            objectFit: 'cover',
            flexShrink: 0,
          }}
        />

        {/* Form content - 40% height */}
        <Box
          sx={{
            minHeight: '40vh',
            background: 'white',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            px: 2,
            py: 4,
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: 400,
              maxHeight: '100%',
              overflow: 'auto',
            }}
          >
            {/* Logo */}
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <img 
                src={`${process.env.PUBLIC_URL}/favicon/Elogo.png`} 
                alt="Evantage Logo" 
                style={{ 
                  width: '200px', 
                  height: 'auto',
                  // filter: 'brightness(0) saturate(100%) invert(0%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(0%) contrast(100%)'
                }} 
              />
            </Box>

            {/* Welcome Text */}
            <Typography
              variant="h5"
              sx={{
                color: 'black',
                fontWeight: 'bold',
                textAlign: 'center',
                mb: 1,
              }}
            >
              Welcome Back!
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                textAlign: 'center',
                mb: 3,
                fontSize: '14px',
              }}
            >
              Still managing everything manually? <br /> Evantage automates it for you.
            </Typography>

            {/* Form Container */}
            <Box
              sx={{
                width: '100%',
              }}
            >
              {children}
            </Box>
          </Box>
        </Box>

        {/* Remaining space */}
        <Box sx={{ flex: 1, background: 'white' }} />
      </Box>
    );
  }

  // Desktop layout (like SaleSkip)
  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
      }}
    >
      {/* Left Column - Branding with Background Image - 70% width */}
      <Box
        sx={{
          width: '70%',
          background: `url(${defaultImageHomepage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          p: 5,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Overlay for better text readability */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.3)',
            zIndex: 1,
          }}
        />

        {/* Top content */}
        {/* <Box sx={{ position: 'relative', zIndex: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
            }}
          >
          </Box>

          <Typography
            variant="h2"
            sx={{
              color: 'white',
              fontWeight: 'bold',
              mb: 2,
              fontSize: { lg: '48px', xl: '56px' },
              lineHeight: 1.2,
            }}
          >
            Welcome To <br /> {window.REACT_APP_TITLE} !👋
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: 'rgba(255,255,255,0.9)',
              fontSize: '18px',
              lineHeight: 1.6,
              maxWidth: 400,
            }}
          >
            Skip repetitive and manual maintenance tasks. Get highly productive through automation and save tons of time!
          </Typography>
        </Box> */}

        {/* Bottom content */}
        {/* <Box sx={{ position: 'relative', zIndex: 2 }}>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255,255,255,0.8)',
              fontSize: '14px',
            }}
          >
            © 2025 Evantage Solutions Sdn Bhd. All rights reserved.
          </Typography>
        </Box> */}
      </Box>

      {/* Right Column - Login Form - 30% width */}
      <Box
        sx={{
          width: '30%',
          background: 'white',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          p: 5,
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 400,
          }}
        >
          {/* Logo */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <img 
              src={`${process.env.PUBLIC_URL}/favicon/Elogo.png`} 
              alt="Evantage Logo" 
              style={{ 
                width: '200px', 
                height: 'auto',
                // filter: 'brightness(0) saturate(100%) invert(0%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(0%) contrast(100%)'
              }} 
            />
          </Box>

          {/* Welcome Text */}
          <Typography
            variant="h4"
            sx={{
              color: 'black',
              fontWeight: 'bold',
              textAlign: 'center',
              mb: 1,
            }}
          >
            Welcome Back!
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              textAlign: 'center',
              mb: 4,
              fontSize: '16px',
            }}
          >
            Still managing everything manually? <br /> Evantage automates it for you.
            <Link
              href="#"
              sx={{
                color: 'primary.main',
                textDecoration: 'underline',
                fontWeight: 500,
                '&:hover': {
                  color: 'primary.dark',
                },
              }}
            >
              {/* Create a new account now */}
            </Link>
            {/* , it's FREE! Takes less than a minute. */}
          </Typography>

          {/* Form Container */}
          <Box
            sx={{
              width: '100%',
            }}
          >
            {children}
          </Box>

          {/* Footer */}
          {/* <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: '14px',
              }}
            >
              Forget password{' '}
              <Link
                href="#"
                sx={{
                  color: 'primary.main',
                  textDecoration: 'underline',
                  fontWeight: 500,
                  '&:hover': {
                    color: 'primary.dark',
                  },
                }}
              >
                Click here
              </Link>
            </Typography>
          </Box> */}
        </Box>
      </Box>
    </Box>
  );
}

AuthClassicLayout.propTypes = {
  children: PropTypes.node,
  image: PropTypes.string,
  title: PropTypes.string,
};
